<template>
  <div class="homepage">

    <!-- ── Hero ── -->
    <section class="hero-section">
      <div class="hero-blob hero-blob--orange"></div>
      <div class="hero-blob hero-blob--blue"></div>

      <div class="hero-inner">
        <div class="hero-badge">14+ Years in Academics &bull; 8+ Years in IT Startup</div>

        <h1 class="hero-heading">
          Hi, I'm <span class="text-orange">Ravindra Bachate</span>
        </h1>

        <div class="role-switcher">
          <transition name="role-fade" mode="out-in">
            <h2 :key="activeRole" class="role-text">{{ roles[activeRole] }}</h2>
          </transition>
        </div>

        <p class="hero-desc">
          I bridge the gap between academic research and industry practice, designing
          human-centered experiences for emerging technologies while shaping the next
          generation of design leaders.
        </p>

        <div class="hero-actions">
          <router-link :to="{ name: 'work' }" class="btn-primary-pill">
            View My Work <span class="btn-arrow">&rarr;</span>
          </router-link>
          <router-link :to="{ name: 'contact' }" class="btn-outline-pill">Let's Talk</router-link>
        </div>

        <div class="scroll-hint-wrap">
          <span class="scroll-hint-text">Scroll to explore</span>
          <div class="mouse-icon">
            <div class="mouse-dot"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Bento Grid ── -->
    <section class="bento-section reveal">
      <div class="section-header">
        <h2>What I Do</h2>
        <p class="section-sub">A multifaceted approach to design, research, and education</p>
      </div>

      <div class="bento-grid">
        <!-- Large card -->
        <div class="bento-card bento-large">
          <div class="bento-tag">About Me</div>
          <h2>Designing the Future of<br />Human-Computer Interaction</h2>
          <div class="bento-large-footer">
            <div class="stats-grid">
              <div class="stat-chip"><strong>14+</strong><span>Years Experience</span></div>
              <div class="stat-chip"><strong>200+</strong><span>Students Mentored</span></div>
              <div class="stat-chip"><strong>15+</strong><span>Publications</span></div>
              <div class="stat-chip"><strong>60+</strong><span>Projects</span></div>
            </div>
            <div class="profile-photo-frame">
              <div class="profile-photo-shadow"></div>
              <img src="@/assets/RB UX.png" alt="Ravindra Bachate" class="profile-photo" />
            </div>
          </div>
        </div>

        <!-- Research -->
        <div class="bento-card bento-research">
          <div class="bento-icon">📖</div>
          <h3>Research</h3>
          <p>15+ peer-reviewed publications in HCI, AI interfaces, and XR experiences</p>
          <router-link :to="{ name: 'research' }" class="bento-link bento-link--blue">Explore Research &rarr;</router-link>
        </div>

        <!-- Teaching -->
        <div class="bento-card bento-teaching">
          <div class="bento-icon">🎓</div>
          <h3>Teaching</h3>
          <p>Associate Professor &amp; HOD shaping future design leaders</p>
          <router-link :to="{ name: 'mentorship' }" class="bento-link bento-link--purple">View Courses &rarr;</router-link>
        </div>

        <!-- UX Design (wide) -->
        <div class="bento-card bento-ux bento-wide">
          <div class="bento-wide-inner">
            <div class="bento-wide-text">
              <div class="bento-icon">✦</div>
              <h3>UX Design &amp; Development</h3>
              <p>Designing meaningful digital experiences at the intersection of UX, HCI research, and emerging technologies, with experience across enterprise SaaS, healthcare, and IoT ecosystems.</p>
              <router-link :to="{ name: 'work' }" class="bento-link bento-link--green">View Portfolio &rarr;</router-link>
            </div>
            <div class="bento-icon-quad">
              <div class="icon-box">💻</div>
              <div class="icon-box">💡</div>
              <div class="icon-box">🎯</div>
              <div class="icon-box">💼</div>
            </div>
          </div>
        </div>

        <!-- Mentorship -->
        <div class="bento-card bento-mentorship">
          <div class="bento-icon">👥</div>
          <h3>Mentorship</h3>
          <p>1:1 guidance for aspiring designers and researchers</p>
          <router-link :to="{ name: 'mentorship' }" class="bento-link bento-link--yellow">Get Mentorship &rarr;</router-link>
        </div>
      </div>
    </section>

    <!-- ── Journey ── -->
    <section class="journey-section reveal">
      <div class="section-header">
        <h2>My Journey</h2>
        <p class="section-sub">From developer to design leader and educator</p>
      </div>

      <div class="journey-list">
        <div
          v-for="(entry, index) in journey"
          :key="index"
          class="journey-row"
          :class="'journey-color-' + (index % 4)"
        >
          <div class="journey-node">
            <div class="journey-node-icon">{{ entry.icon }}</div>
          </div>
          <div class="journey-card-new">
            <span class="journey-year-badge">{{ entry.year }}</span>
            <h3>{{ entry.company }}</h3>
            <p class="journey-role-title">{{ entry.title }}</p>
            <p class="journey-desc">{{ entry.description }}</p>
            <p class="journey-meta">📍 {{ entry.location }} &bull; {{ entry.type }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Skills ── -->
    <section class="skills-section reveal">
      <div class="section-header">
        <h2>Expertise &amp; Skills</h2>
        <p class="section-sub">A comprehensive skill set spanning design, research, development, and education</p>
      </div>
      <div class="skills-grid">
        <div
          v-for="(skill, index) in skills"
          :key="index"
          class="skill-tag"
          :class="'skill-color-' + (index % 6)"
        >{{ skill }}</div>
      </div>
    </section>

    <!-- ── Testimonials ── -->
    <section class="testimonials-section reveal">
      <div class="container">
        <div class="section-header">
          <h2>Voices of Trust</h2>
        </div>
        <div class="row justify-content-center">
          <div
            v-for="testimonial in groupedTestimonials[currentGroupIndex]"
            :key="testimonial.author"
            class="col-md-4 mb-4"
          >
            <div
              class="card shadow-sm border-0 rounded-4 p-4 h-100"
              :style="{ borderTop: '5px solid ' + testimonialColors[testimonials.indexOf(testimonial) % testimonialColors.length] }"
            >
              <div class="d-flex align-items-center mb-3">
                <img :src="testimonial.image" alt="Photo" class="rounded-circle me-3" width="50" height="50" style="object-fit:cover" />
                <div>
                  <h6 class="mb-0 fw-bold">{{ testimonial.author }}</h6>
                  <small class="text-muted">{{ testimonial.job }}</small>
                </div>
              </div>
              <p class="text-muted small mb-0">{{ testimonial.text }}</p>
            </div>
          </div>
        </div>
        <div class="text-center mt-2">
          <span
            v-for="(_, index) in groupedTestimonials"
            :key="index"
            class="dot"
            :class="{ active: index === currentGroupIndex }"
            @click="goToGroup(index)"
          />
        </div>
      </div>
    </section>

    <!-- ── CTA ── -->
    <section class="cta-section reveal">
      <div class="cta-inner">
        <h2>Let's Create Something<br /><span class="text-orange">Extraordinary Together</span></h2>
        <p>Whether it's a research collaboration, design project, speaking engagement, or mentorship — I'm excited to hear from you.</p>
        <router-link :to="{ name: 'contact' }" class="btn-primary-pill btn-primary-pill--lg">
          Start a Conversation &rarr;
        </router-link>
      </div>
    </section>

  </div>
</template>

<script>
// Images still need webpack require() — only data is fetched at runtime
const imageMap = require.context('@/assets', false, /\.(png|jpe?g|gif|svg|webp)$/);
function assetUrl(filename) {
  try { return imageMap('./' + filename); } catch { return ''; }
}

export default {
  name: 'HomePage',
  data() {
    return {
      activeRole: 0,
      roles: [],
      skills: [],
      journey: [],
      testimonials: [],
      testimonialColors: ['#fd7623', '#4a6cf7', '#3cb98d'],
      currentGroupIndex: 0,
      testimonialInterval: null,
    };
  },
  computed: {
    groupedTestimonials() {
      const groups = [];
      for (let i = 0; i < this.testimonials.length; i += 3) {
        groups.push(this.testimonials.slice(i, i + 3));
      }
      return groups;
    },
  },
  methods: {
    goToGroup(index) {
      this.currentGroupIndex = index;
      this.stopTestimonialRotation();
      this.startTestimonialRotation();
    },
    startTestimonialRotation() {
      this.testimonialInterval = setInterval(() => {
        this.currentGroupIndex = (this.currentGroupIndex + 1) % this.groupedTestimonials.length;
      }, 5000);
    },
    stopTestimonialRotation() {
      if (this.testimonialInterval) clearInterval(this.testimonialInterval);
    },
    setupScrollReveal() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('is-visible');
          });
        },
        { threshold: 0, rootMargin: '0px 0px -40px 0px' }
      );
      document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    },
  },
  async mounted() {
    try {
      const url = process.env.BASE_URL + 'data/ravi.json';
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`);
      const data = await res.json();
      this.roles     = data.roles;
      this.skills    = data.skills;
      this.journey   = data.journey;
      this.testimonials = data.testimonials.map(t => ({ ...t, image: assetUrl(t.image) }));
    } catch (err) {
      console.error('home.json load failed:', err);
    }

    setInterval(() => {
      this.activeRole = (this.activeRole + 1) % this.roles.length;
    }, 2000);

    this.startTestimonialRotation();
    this.$nextTick(() => this.setupScrollReveal());
  },
  beforeUnmount() {
    this.stopTestimonialRotation();
  },
};
</script>

<style scoped>
/* ── Reset ── */
.homepage {
  margin: 0;
  padding: 0;
  background: var(--color-bg-white);
}

/* ── Shared helpers ── */
.text-orange { color: var(--color-primary); }

.section-header {
  text-align: center;
  margin-bottom: var(--space-12);
}
.section-header h2 {
  font-size: var(--text-4xl);
  font-weight: var(--font-extrabold);
  color: var(--color-text-900);
  margin-bottom: var(--space-2);
}
.section-sub {
  font-size: var(--text-lg);
  color: var(--color-text-400);
  max-width: 600px;
  margin: 0 auto;
}

/* ── Scroll-reveal ── */
.reveal {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
  will-change: opacity, transform;
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
  will-change: auto;
}
/* On mobile, skip the animation entirely to avoid scroll interference */
@media (max-width: 768px) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ══════════════════════════════
   HERO
══════════════════════════════ */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-bg-light) 0%, var(--color-bg-white) 50%, var(--color-primary-subtle) 100%);
  overflow: clip;
  padding: 6rem var(--space-6) var(--space-16);
}

/* Animated blobs */
.hero-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: blobFloat 8s ease-in-out infinite;
  pointer-events: none;
}
.hero-blob--orange {
  width: 480px;
  height: 480px;
  background: rgba(241, 129, 40, 0.18);
  top: -60px;
  right: -80px;
  animation-delay: 0s;
}
.hero-blob--blue {
  width: 400px;
  height: 400px;
  background: rgba(99, 102, 241, 0.12);
  bottom: -60px;
  left: -80px;
  animation-delay: 3s;
}
@keyframes blobFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -20px) scale(1.05); }
}

.hero-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  width: 100%;
}

.hero-badge {
  display: inline-block;
  padding: var(--space-2) var(--space-5);
  background: var(--color-bg-white);
  border: 2px solid var(--color-primary-border);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-text-500);
  margin-bottom: 1.75rem;
}

.hero-heading {
  font-size: clamp(3.5rem, 7vw, 4.5rem);
  font-weight: 900;
  color: var(--color-bg-darker);
  line-height: var(--leading-tight);
  margin-bottom: var(--space-5);
}

/* Role switcher */
.role-switcher {
  height: 7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-6);
  overflow: hidden;
}
.role-text {
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: var(--font-bold);
  color: var(--color-text-700);
  margin: 0;
}
.role-fade-enter-active,
.role-fade-leave-active {
  transition: opacity var(--duration-slow) var(--ease-out), transform var(--duration-slow) var(--ease-out);
}
.role-fade-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.role-fade-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}

.hero-desc {
  font-size: var(--text-xl);
  color: var(--color-text-400);
  line-height: var(--leading-loose);
  max-width: 620px;
  margin: 0 auto var(--space-10);
}

.hero-actions {
  display: flex;
  gap: var(--space-4);
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: var(--space-12);
}

.btn-primary-pill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--color-primary);
  color: var(--color-bg-white);
  padding: 0.85rem var(--space-8);
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
  font-size: var(--text-base);
  text-decoration: none;
  transition: background var(--duration-base) var(--ease-out), box-shadow var(--duration-base) var(--ease-out), transform var(--duration-fast) var(--ease-out);
  box-shadow: var(--shadow-orange);
}
.btn-primary-pill:hover {
  background: var(--color-primary-dark);
  box-shadow: var(--shadow-orange-lg);
  transform: translateY(-2px);
  color: var(--color-bg-white);
}
.btn-primary-pill--lg {
  font-size: var(--text-lg);
  padding: var(--space-4) var(--space-10);
}
.btn-arrow { transition: transform var(--duration-base) var(--ease-out); }
.btn-primary-pill:hover .btn-arrow { transform: translateX(4px); }

.btn-outline-pill {
  display: inline-flex;
  align-items: center;
  background: var(--color-bg-white);
  color: var(--color-text-700);
  padding: 0.85rem var(--space-8);
  border-radius: var(--radius-full);
  font-weight: var(--font-semibold);
  font-size: var(--text-base);
  border: 2px solid var(--color-border-strong);
  text-decoration: none;
  transition: border-color var(--duration-base) var(--ease-out), color var(--duration-base) var(--ease-out);
}
.btn-outline-pill:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Scroll hint */
.scroll-hint-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-300);
}
.scroll-hint-text { font-size: var(--text-sm); }
.mouse-icon {
  width: 26px;
  height: 42px;
  border: 2px solid var(--color-border-strong);
  border-radius: 13px;
  display: flex;
  justify-content: center;
  padding-top: 6px;
}
.mouse-dot {
  width: 6px;
  height: 6px;
  background: var(--color-text-300);
  border-radius: 50%;
  animation: mouseDrop 1.6s ease-in-out infinite;
}
@keyframes mouseDrop {
  0%, 100% { transform: translateY(0); opacity: 1; }
  60% { transform: translateY(10px); opacity: 0.3; }
}

/* ══════════════════════════════
   BENTO GRID
══════════════════════════════ */
.bento-section {
  padding: var(--section-py) var(--space-6);
  background: var(--color-bg-white);
  max-width: var(--container-xl);
  margin: 0 auto;
}

.bento-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--bento-gap);
}

/* Card base */
.bento-card {
  border-radius: var(--radius-2xl);
  padding: var(--space-8);
  border: 1px solid transparent;
  transition: box-shadow var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.bento-card:hover {
  box-shadow: var(--shadow-xl);
  transform: translateY(-3px);
}

/* Large card — spans 2 cols × 2 rows */
.bento-large {
  grid-column: 1 / 3;
  grid-row: 1 / 3;
  background: linear-gradient(135deg, var(--color-primary-subtle), var(--color-primary-border));
  border-color: var(--color-primary-border);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.bento-tag {
  display: inline-block;
  background: rgba(255,255,255,0.8);
  backdrop-filter: blur(4px);
  border-radius: var(--radius-full);
  padding: 0.4rem 1.25rem;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-700);
  margin-bottom: var(--space-4);
  width: fit-content;
}
.bento-large h3 {
  font-size: var(--text-2xl);
  font-weight: var(--font-extrabold);
  color: var(--color-bg-darker);
  line-height: var(--leading-snug);
  margin-bottom: var(--space-6);
}
.bento-large-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-6);
  flex-wrap: nowrap;
}
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
  flex: 1;
}
.stat-chip {
  background: rgba(255,255,255,0.75);
  backdrop-filter: blur(4px);
  border-radius: var(--radius-xl);
  padding: var(--space-4) var(--space-5);
  display: flex;
  flex-direction: column;
}
.stat-chip strong {
  font-size: var(--text-3xl);
  font-weight: var(--font-extrabold);
  color: var(--color-primary);
  line-height: 1;
}
.stat-chip span {
  font-size: var(--text-sm);
  color: var(--color-text-500);
  margin-top: var(--space-1);
  font-weight: var(--font-medium);
}

/* Profile photo inside large card */
.profile-photo-frame {
  position: relative;
  width: 280px;
  height: 280px;
  flex-shrink: 0;
  border-radius: var(--radius-xl);
}
.profile-photo {
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
}

/* Research */
.bento-research {
  grid-column: 3;
  grid-row: 1;
  background: var(--color-blue-50);
  border-color: var(--color-blue-100);
}
/* Teaching */
.bento-teaching {
  grid-column: 3;
  grid-row: 2;
  background: var(--color-purple-50);
  border-color: var(--color-purple-100);
}
/* UX Design — wide, 2 cols */
.bento-ux {
  grid-column: 1 / 3;
  grid-row: 3;
  background: linear-gradient(135deg, var(--color-green-50), var(--color-teal-50));
  border-color: var(--color-green-100);
}
/* Mentorship */
.bento-mentorship {
  grid-column: 3;
  grid-row: 3;
  background: var(--color-yellow-50);
  border-color: var(--color-yellow-100);
}

.bento-icon {
  font-size: var(--text-2xl);
  margin-bottom: var(--space-4);
}
.bento-card h3 {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: var(--space-2);
}
.bento-card p {
  font-size: var(--text-sm);
  color: var(--color-text-500);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-4);
}
.bento-link {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  text-decoration: none;
  transition: gap var(--duration-base) var(--ease-out);
}
.bento-link--blue   { color: var(--color-blue-600); }
.bento-link--purple { color: var(--color-purple-600); }
.bento-link--green  { color: var(--color-green-600); }
.bento-link--yellow { color: var(--color-yellow-600); }

.bento-wide-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-8);
}
.bento-wide-text { flex: 1; }
.bento-icon-quad {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  flex-shrink: 0;
}
.icon-box {
  width: 60px;
  height: 60px;
  background: var(--color-bg-white);
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-xl);
  box-shadow: var(--shadow-xs);
}

/* ══════════════════════════════
   JOURNEY
══════════════════════════════ */
.journey-section {
  padding: var(--section-py) var(--space-6);
  background: var(--color-bg-light);
}
.journey-list {
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}
.journey-row {
  display: flex;
  gap: var(--space-5);
  align-items: flex-start;
}
.journey-node {
  flex-shrink: 0;
  padding-top: 0.25rem;
}
.journey-node-icon {
  width: 52px;
  height: 52px;
  border-radius: var(--radius-xl);
  border: 3px solid var(--color-bg-white);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-base);
  background: var(--color-bg-white);
}
.journey-color-0 .journey-node-icon { background: var(--color-primary-subtle); }
.journey-color-1 .journey-node-icon { background: var(--color-blue-50); }
.journey-color-2 .journey-node-icon { background: var(--color-green-50); }
.journey-color-3 .journey-node-icon { background: var(--color-purple-50); }

.journey-card-new {
  flex: 1;
  background: var(--color-bg-white);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-xs);
}
.journey-year-badge {
  display: inline-block;
  background: var(--color-bg-subtle);
  color: var(--color-text-700);
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-full);
  margin-bottom: 0.75rem;
}
.journey-color-0 .journey-year-badge { background: var(--color-primary-subtle); color: var(--color-primary-dark); }
.journey-color-1 .journey-year-badge { background: var(--color-blue-50); color: var(--color-blue-700); }
.journey-color-2 .journey-year-badge { background: var(--color-green-50); color: var(--color-green-600); }
.journey-color-3 .journey-year-badge { background: var(--color-purple-50); color: var(--color-purple-600); }

.journey-card-new h3 {
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  color: var(--color-bg-darker);
  margin-bottom: 0.25rem;
}
.journey-role-title {
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-text-400);
  margin-bottom: 0.6rem;
}
.journey-desc {
  font-size: var(--text-sm);
  color: var(--color-text-500);
  line-height: var(--leading-relaxed);
  margin-bottom: 0.6rem;
}
.journey-meta {
  font-size: var(--text-xs);
  color: var(--color-text-300);
  margin: 0;
}

/* ══════════════════════════════
   SKILLS
══════════════════════════════ */
.skills-section {
  padding: var(--section-py) var(--space-6);
  background: var(--color-bg-white);
  max-width: 1100px;
  margin: 0 auto;
}
.skills-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-4);
}
.skill-tag {
  padding: var(--space-3) var(--space-3);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  text-align: center;
  font-weight: var(--font-medium);
  cursor: default;
  letter-spacing: var(--tracking-wide);
  transition: transform var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out);
}
.skill-tag:hover { transform: scale(1.05); box-shadow: var(--shadow-md); }
.skill-color-0 { background: var(--color-primary-subtle); color: var(--color-primary-dark); border: 1px solid var(--color-primary-border); }
.skill-color-1 { background: var(--color-blue-50); color: var(--color-blue-700); border: 1px solid var(--color-blue-100); }
.skill-color-2 { background: var(--color-purple-50); color: var(--color-purple-600); border: 1px solid var(--color-purple-100); }
.skill-color-3 { background: var(--color-green-50); color: var(--color-green-600); border: 1px solid var(--color-green-100); }
.skill-color-4 { background: var(--color-pink-50); color: var(--color-pink-600); border: 1px solid var(--color-pink-100); }
.skill-color-5 { background: var(--color-indigo-50); color: var(--color-indigo-600); border: 1px solid var(--color-indigo-100); }

/* ══════════════════════════════
   TESTIMONIALS
══════════════════════════════ */
.testimonials-section {
  padding: var(--section-py) var(--space-6);
  background: var(--color-bg-light);
}
.testimonials-section .section-header {
  margin-bottom: var(--space-10);
}
.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-border-strong);
  margin: 0 4px;
  cursor: pointer;
  transition: background var(--duration-base) var(--ease-out), transform var(--duration-base) var(--ease-out);
}
.dot.active {
  background: var(--color-primary);
  transform: scale(1.3);
}

/* ══════════════════════════════
   CTA
══════════════════════════════ */
.cta-section {
  padding: var(--section-py) var(--space-6);
  background: linear-gradient(135deg, var(--color-primary-subtle) 0%, var(--color-bg-white) 100%);
}
.cta-inner {
  max-width: 680px;
  margin: 0 auto;
  text-align: center;
}
.cta-inner h2 {
  font-size: var(--text-3xl);
  font-weight: var(--font-extrabold);
  color: var(--color-bg-darker);
  line-height: var(--leading-snug);
  margin-bottom: var(--space-5);
}
.cta-inner p {
  font-size: var(--text-lg);
  color: var(--color-text-400);
  line-height: var(--leading-loose);
  margin-bottom: var(--space-8);
}

/* ══════════════════════════════
   DARK MODE FIXES
   (hardcoded rgba values and Bootstrap overrides that
   don't automatically pick up CSS variable changes)
══════════════════════════════ */

/* 0 — Journey node icons: border was var(--color-bg-white) which goes dark,
        making the circle invisible. Give it a visible border + slight contrast bg. */
[data-theme="dark"] .journey-node-icon {
  border-color: var(--color-border-strong);
  background: var(--color-border-strong) !important;
}

/* 1 — "About Me" pill tag: was rgba(255,255,255,0.8) — now dark-friendly */
[data-theme="dark"] .bento-tag {
  background: rgba(255, 255, 255, 0.10);
  color: var(--color-text-400);
}

/* 2 — stat-chips (Years exp, Students, Publications, Projects):
        background was hardcoded rgba(255,255,255,0.75) */
[data-theme="dark"] .stat-chip {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme="dark"] .stat-chip span {
  color: var(--color-text-400);
}

/* 3 — "Voices of Trust" heading: force light color over any Bootstrap override */
[data-theme="dark"] .testimonials-section .section-header h2 {
  color: var(--color-text-900);
}

/* 3b — Bootstrap .card inside testimonials: override card bg and text */
[data-theme="dark"] .testimonials-section .card {
  background-color: var(--color-bg-subtle);
  border-color: var(--color-border) !important;
}
[data-theme="dark"] .testimonials-section .card .fw-bold {
  color: var(--color-text-700);
}
[data-theme="dark"] .testimonials-section .card .text-muted {
  color: var(--color-text-400) !important;
}

/* ══════════════════════════════
   RESPONSIVE
══════════════════════════════ */
@media (max-width: 900px) {
  .skills-grid { grid-template-columns: repeat(3, 1fr); }
  .bento-grid {
    grid-template-columns: 1fr 1fr;
  }
  .bento-large {
    grid-column: 1 / 3;
    grid-row: 1;
  }
  .bento-research { grid-column: 1; grid-row: 2; }
  .bento-teaching  { grid-column: 2; grid-row: 2; }
  .bento-ux        { grid-column: 1 / 3; grid-row: 3; }
  .bento-mentorship { grid-column: 1 / 3; grid-row: 4; }
  .profile-photo-frame { width: 180px; height: 180px; }
}

@media (max-width: 600px) {
  .bento-grid {
    grid-template-columns: 1fr;
  }
  .bento-large,
  .bento-research,
  .bento-teaching,
  .bento-ux,
  .bento-mentorship {
    grid-column: 1;
    grid-row: auto;
  }
  .bento-wide-inner { flex-direction: column; }
  .bento-icon-quad { display: none; }
  .profile-photo-frame { width: 180px; height: 180px; }
  .bento-large-footer { flex-wrap: wrap; }
  .skills-grid { grid-template-columns: repeat(2, 1fr); }
  .hero-actions { flex-direction: column; align-items: center; }
  .section-header h2 { font-size: var(--text-2xl); }
  .journey-section,
  .skills-section,
  .bento-section,
  .testimonials-section,
  .cta-section { padding: var(--space-12) var(--space-4); }
  .bento-large-footer { flex-direction: column; align-items: flex-start; }
}
</style>
